import React from 'react';
import { Section, KV, Pill, btnNeutral } from 'features/settings/components/panels/askCreditsShared';

/**
 * The live plan, stated plainly, above the upgrade options.
 *
 * Without this the pricing view opened straight into a wall of plans with no
 * statement of what the team is on right now — and a lapsed customer had it
 * worse: the page read their cancelled subscription as current and showed a
 * "Next payment" date they would never be charged on.
 *
 * A team that used to pay keeps its billing history reachable here. Losing
 * access to your own receipts because you cancelled is its own bug, and the
 * Stripe portal behind "Manage billing" is where those invoices live.
 */

const MUTED = 'var(--text-muted)';

const PLAN_LABELS = {
  free: 'Free',
  pro: 'Pro',
  trial: 'Pro trial',
  enterprise: 'Enterprise',
};

/**
 * A past date, or nothing.
 *
 * `currentPeriodEnd` is the period the subscription was PAID through, which
 * for one cancelled early sits in the future — an annual plan cancelled today
 * still carries next year's date. Rendering it after "ended on" produced
 * "ended on May 15, 2027". A date we cannot stand behind is worse than no
 * date, so a future one is dropped and the sentence stands without it.
 */
function formatPastDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime()) || d.getTime() > Date.now()) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const CurrentPlanBanner = ({
  plan,
  priorPlan,
  priorStatus,
  endedOn,
  hasStripeCustomer,
  onManageSubscription,
  manageLoading,
}) => {
  const priorLabel = PLAN_LABELS[priorPlan];
  const ended = formatPastDate(endedOn);

  // A past-due subscription has not ended — the payment failed and the card is
  // still fixable. Telling that customer their plan "ended" would send them to
  // re-subscribe when all they need is to update a card.
  const isPastDue = String(priorStatus || '').toLowerCase() === 'past_due';

  // Past due names the plan they are ON, not the access they have right now.
  //
  // `plan` is the live entitlement and reads 'free' the moment a payment fails,
  // which is correct for gating and wrong as a heading: the banner rendered
  // "Free" directly above its own sentence "Your Pro subscription is past due"
  // (TRO-280). Two elements of one card disagreeing is not a cosmetic problem —
  // the customer cannot tell whether they still have a subscription to rescue,
  // and the whole point of this state is that they do.
  //
  // LABEL ONLY. Access, the pricing grid and every limit still derive from
  // `plan`; nothing here grants anything.
  const planLabel = (isPastDue && priorLabel) || PLAN_LABELS[plan] || 'Free';

  const priorNote = (() => {
    if (!priorLabel) return null;
    if (isPastDue) {
      return `Your ${priorLabel} subscription is past due. Update your payment method to restore it.`;
    }
    return ended
      ? `Your ${priorLabel} subscription ended on ${ended}.`
      : `Your ${priorLabel} subscription has ended.`;
  })();

  // The pill was a hardcoded green "Active" for every team that reached this
  // banner. Next to a note reading "your subscription is past due", the page
  // was contradicting itself in adjacent elements — and green is the one colour
  // that should never sit beside a failed payment.
  const pill = isPastDue
    ? { label: 'Past due', color: 'var(--feedback-error)', bg: 'var(--feedback-error-bg)' }
    : { label: 'Active', color: 'var(--feedback-success)', bg: 'var(--feedback-success-bg)' };

  return (
    <Section
      title="Plan"
      action={hasStripeCustomer ? (
        <button type="button" onClick={onManageSubscription} disabled={manageLoading} className={btnNeutral}>
          {manageLoading ? 'Opening…' : 'Billing history'}
        </button>
      ) : null}
    >
      <KV label="Current plan">
        <span className="inline-flex items-center gap-2">
          {planLabel}
          <Pill {...pill} />
        </span>
      </KV>
      {priorNote && <p className="mt-3 text-[12px]" style={{ color: MUTED }}>{priorNote}</p>}
    </Section>
  );
};

export default CurrentPlanBanner;
